import type { DateRange } from '@/components/ui/FilterBar';

import {
  METRICS,
  TREND_CATEGORIES,
  type ActivityId,
  type DailyPoint,
  type DashboardData,
  type MetricId,
  type TrendTab,
} from './dashboardData';
import { eachDay, previousPeriod } from './dateRange';

/**
 * Example numbers for the dashboard until the analytics service exists.
 *
 * Deterministic: the generator is seeded from the range, so the same range
 * always gives the same numbers. Screenshots and tests stay stable, and a
 * Super Admin who reloads does not see the dashboard change on its own. A
 * different range gives visibly different numbers, so the range control is
 * seen to do something.
 *
 * Every number is a whole count, and each day's trend points add up to the
 * period's counts, so the trend and the tables agree.
 */

/** About how many of each happen on an ordinary day. */
const DAILY_SCALE: Record<
  Exclude<MetricId, 'totalSupporters' | 'totalMembers'>,
  number
> = {
  arcViews: 120,
  contentViews: 60,
  campaignViews: 90,
  callClicks: 14,
  emailClicks: 22,
  guideViews: 40,
  surveyViews: 18,
  surveySubmissions: 6,
  documentViews: 25,
  documentDownloads: 8,
  volunteerClicks: 9,
  volunteerSubmissions: 3,
  fundraisingClicks: 7,
  newSupporters: 12,
  newMembers: 2,
  followedSupporters: 10,
  invitesAccepted: 3,
  notificationsDelivered: 300,
  notificationOpens: 90,
  notificationClicks: 20,
  orgContentViews: 35,
};

type DailyMetric = keyof typeof DAILY_SCALE;

/** The supporters and members there were before the previous period began. */
const BASE_SUPPORTERS = 11_800;
const BASE_MEMBERS = 140;

/** Quieter at weekends, as engagement with an advocacy app tends to be. */
const WEEKDAY_SHAPE = [0.7, 1.05, 1.1, 1.1, 1.05, 1, 0.75];

/** A small, fast, well-spread generator: mulberry32. */
function generator(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a, to turn the range into a seed. */
function hash(text: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}

function weekday(day: string): number {
  return new Date(`${day}T00:00:00Z`).getUTCDay();
}

/** One metric's count for every day of a period. */
function dailyCounts(
  days: string[],
  scale: number,
  level: number,
  random: () => number,
): number[] {
  return days.map((day) =>
    Math.max(
      0,
      Math.round(scale * level * WEEKDAY_SHAPE[weekday(day)] * (0.7 + random() * 0.6)),
    ),
  );
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

export function sampleDashboard(range: DateRange): DashboardData {
  const random = generator(hash(range.from + range.to));
  const previous = previousPeriod(range);
  const days = eachDay(range);
  const previousDays = eachDay(previous);

  const daily = {} as Record<DailyMetric, number[]>;
  const counts = {} as Record<DailyMetric, { current: number; previous: number }>;

  for (const id of Object.keys(DAILY_SCALE) as DailyMetric[]) {
    // Each metric drifts on its own between the two periods - mostly up, some
    // down - so the cards show a believable mix of changes.
    const drift = 0.85 + random() * 0.35;
    const current = dailyCounts(days, DAILY_SCALE[id], drift, random);
    const before = dailyCounts(previousDays, DAILY_SCALE[id], 1, random);
    daily[id] = current;
    counts[id] = { current: sum(current), previous: sum(before) };
  }

  // Totals at the end of each period: the base, plus everyone who joined since.
  const supportersBefore = BASE_SUPPORTERS + counts.newSupporters.previous;
  const membersBefore = BASE_MEMBERS + counts.newMembers.previous;
  const metrics: DashboardData['metrics'] = {
    ...counts,
    totalSupporters: {
      current: supportersBefore + counts.newSupporters.current,
      previous: supportersBefore,
    },
    totalMembers: {
      current: membersBefore + counts.newMembers.current,
      previous: membersBefore,
    },
  };

  const byCategory = Object.fromEntries(
    TREND_CATEGORIES.map((category) => {
      const members = METRICS.filter((metric) => metric.trend === category).map(
        (metric) => metric.id as DailyMetric,
      );
      const points: DailyPoint[] = days.map((date, index) => ({
        date,
        value: sum(members.map((id) => daily[id][index])),
      }));
      return [category, points];
    }),
  ) as Record<Exclude<TrendTab, 'overview'>, DailyPoint[]>;

  const series: DashboardData['series'] = {
    overview: days.map((date, index) => ({
      date,
      value: sum(TREND_CATEGORIES.map((category) => byCategory[category][index].value)),
    })),
    ...byCategory,
  };

  return {
    source: 'sample',
    range: { from: range.from, to: range.to },
    previous,
    metrics,
    series,
    activity: sampleActivity(),
  };
}

/**
 * What is live now does not depend on the range, so it has a fixed seed: the
 * counts stay put while the range changes, as real ones would.
 */
function sampleActivity(): Record<ActivityId, number> {
  const random = generator(hash('current-activity'));
  const between = (low: number, high: number) =>
    low + Math.floor(random() * (high - low + 1));
  return {
    activeCampaigns: between(3, 8),
    currentGuides: between(1, 4),
    openSurveys: between(2, 6),
    scheduledNotifications: between(1, 5),
    pendingInvitations: between(10, 40),
  };
}
